import {of as observableOf,  Observer ,  Observable } from 'rxjs';
import {filter, catchError, retryWhen, delay, map, exhaustMap, take, withLatestFrom, takeUntil, mergeMap, tap, share, distinctUntilChanged} from 'rxjs/operators';
import { environment } from '../../../../../apps/hub/src/environments/environment';
import { UsersAction } from './../users/users.actions';
import { RouterTrackerService } from './../../services/router-tracker/router-tracker.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DefaultResponse } from './../../models/form/DefaultResponse';
import { RoutesTable } from './../../resources/routes-table.model';
import { BackendService } from '../../services/backend/backend.service';
import { INIT, Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppState } from '../../interfaces/app-state.interface';
import { Injectable, NgZone } from '@angular/core';
import { UserManagerService } from '../../services/users/user-manager.service';
import { InitializerAction } from './initiliazer.action';
import { InternetService } from '../../services/internet/internet.service';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { UsersEffects } from '../users/users.effects';
import { FinanceActions } from '../finance/finance.action';
import { Roles } from '../../models/roles/roles.enum';
import { DigitalSubscriptionAction } from '../digital-subscription/digital-subscription.action';

@Injectable()
export class InitializerEffects {
    /**
     * Inicialização da store (disparada quando as configurações do ambiente são carregadas)
     */
    storeInitialize$ = createEffect(() => this.actions$
    .pipe(
      ofType(InitializerAction.settingsLoaded),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      take(1),
      map((action: any) => {

        if (action.payload.ignorePreviousUser) {
            this.userManager.clearUserData();
        } else {
            const payload = UserManagerService.getPreviousUserData();
            if (payload) {

                // tslint:disable-next-line
                console.warn(`[App Lifecycle Info] Previous user found (${payload.name}). Saving data to store.`);
                return { type: UsersAction.loginSuccess, payload };
            }
        }
        this.store$.dispatch({ type: InitializerAction.getSessionID, payload: action.payload.settings });
        return { type: InitializerAction.noPreviousUserFound };
    }),
    share()));

    settingsLoaded$ = createEffect(() => this.actions$
    .pipe(
      ofType(InitializerAction.settingsLoaded),
      share()), { dispatch: false });

    /**
     * Session id recuperada, realizar conexão com o signalR
     */
    sessionIdReceived$ = createEffect(() => this.actions$
    .pipe(
      ofType(InitializerAction.getSessionIDSuccess),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap((action: any) => {
        this.store$.dispatch({type: InitializerAction.connectToRealTime, payload: action.payload});
        this.store$.dispatch({ type: DigitalSubscriptionAction.getPurchase });
    })), {dispatch: false});

    /**
     * Recupera uma session Id para o usuário
     */
    getSessionID$ = createEffect(() => this.actions$
    .pipe(
      ofType(InitializerAction.getSessionID),
        exhaustMap((action: any) => {
            const settings = action.payload;
            // Use BackendService with relative URL for proxy support
            const relativeUrl = RoutesTable.composeUri(RoutesTable.authentication.routes.getSessionID, settings.ci);
            return this.backendService.get<DefaultResponse>(relativeUrl)
                .pipe(
                    map((response: DefaultResponse) => {
                        this.store$.dispatch({ type: UsersAction.saveSessionID, payload: response.value });
                        return { type: InitializerAction.getSessionIDSuccess, payload: response.value };
                    }),
                    takeUntil(this.usersEffects.forceAuthenticationRequest$
                      .pipe(filter((a) => a.type === UsersAction.forcedAuthenticationSuccefully))),
                    retryWhen((error) => {
                        return Observable.create((observer: Observer<any>) => {
                            error
                            .pipe(delay(5000), take(100))
                            .subscribe((data) => observer.next(data), () => {
                                // tslint:disable-next-line: no-console
                                console.error('Cant get sessionID');
                                observer.error(error);
                            });
                        });
                    }),
                    catchError((error) => {
                        return observableOf({ type: InitializerAction.getSessionIDError, payload: error });
                    }),
                );
    }),
    share()));

    public currentUserChanged$ = this.actions$
    .pipe(
      ofType(UsersAction.loginSuccess, UsersAction.logout),
    );
    /**
     * Abri conexão com o signalR no backend
     */
    connectToRealTime$ = createEffect(() => this.actions$
    .pipe(
      ofType(InitializerAction.connectToRealTime),
        mergeMap((action: any) => {
            return this.realTimeService.connect(action.payload, false, true)
            .pipe(
                withLatestFrom(this.user$),
                map(([result, user]) => {

                    if ( user && (user.role === Roles.Activated || user.role === Roles.Registered) ) {
                        this.store$.dispatch({ type: FinanceActions.getRealtimeConsolidatedPosition });
                        this.store$.dispatch({ type: FinanceActions.getRealtimeFinancePosition });
                    }

                    return {type: InitializerAction.connectToRealTimeSuccess};
                }),
                retryWhen((error) => {
                    return Observable.create( (observer: Observer<any>) => {
                    error.pipe(delay(20000),
                            takeUntil(this.currentUserChanged$),
                            take(250))
                            .subscribe( (data) => {
                                // tslint:disable-next-line: no-console
                                console.warn(`${data}. (FIRST CONNECTION) Trying to connect to signalr again...`);
                                observer.next(data);
                            });
                    });
                }),
                catchError((error) => observableOf({type: InitializerAction.connectToRealTimeError, payload: error})));
    }), share()));

    private user$ = this.store$.select((s) => s.authentication.currentUser);

    constructor(
      private actions$: Actions,
      private store$: Store<AppState>,
      private internetService: InternetService,
      private realTimeService: RealtimeService,
      private usersEffects: UsersEffects,
      private userManager: UserManagerService,
      private backendService: BackendService,
  ) {
  }

}
